import { fetchJson } from '@shared/api';
import { SimpleCache } from '@shared/api/cache';
import { CACHE_TTL, CACHE_LIMITS } from '@shared/config';
import type { CategorySlug, Category } from '@features/category-navigation';
import type { Source } from '@features/sources';
import type { SummaryResponse, SummaryHistoryItem } from '../types';
import type { RawSummaryResponse } from './types';
import { transformSummaryResponse } from './transforms';

// Create cache instances for summary-related data
const summaryCache = new SimpleCache<SummaryResponse>({
  defaultTtl: CACHE_TTL.SUMMARY,
  maxEntries: CACHE_LIMITS.SUMMARY_MAX_ENTRIES,
});

const categoryCache = new SimpleCache<Category[]>({
  defaultTtl: CACHE_TTL.CATEGORY,
  maxEntries: CACHE_LIMITS.CATEGORY_MAX_ENTRIES,
});

const sourceCache = new SimpleCache<Source[]>({
  defaultTtl: CACHE_TTL.SOURCE,
  maxEntries: CACHE_LIMITS.SOURCE_MAX_ENTRIES,
});

const historyCache = new SimpleCache<SummaryHistoryItem[]>({
  defaultTtl: CACHE_TTL.HISTORY,
  maxEntries: CACHE_LIMITS.HISTORY_MAX_ENTRIES,
});

interface HealthStatus {
  status: string;
  timestamp: string;
}

const healthCache = new SimpleCache<HealthStatus>({
  defaultTtl: CACHE_TTL.HEALTH,
  maxEntries: CACHE_LIMITS.HEALTH_MAX_ENTRIES,
});

export const summaryApi = {
  /**
   * Get the latest summary for a category
   */
  async getSummaryLatest(category: CategorySlug): Promise<SummaryResponse> {
    const endpoint = `/api/summaries/${category}/latest`;
    const response = await fetchJson<RawSummaryResponse>(endpoint);
    return transformSummaryResponse(response);
  },

  /**
   * Get summary for a specific date and optional edition
   */
  async getSummaryByDate(
    category: CategorySlug,
    date: string,
    edition?: string
  ): Promise<SummaryResponse> {
    let endpoint = `/api/summaries/${category}/${date}`;
    if (edition) {
      endpoint += `?edition=${encodeURIComponent(edition)}`;
    }
    const response = await fetchJson<RawSummaryResponse>(endpoint);
    return transformSummaryResponse(response);
  },

  /**
   * Get today's summary (convenience method)
   */
  async getSummaryToday(category: CategorySlug): Promise<SummaryResponse> {
    return this.getSummaryLatest(category);
  },

  async getCategories(): Promise<Category[]> {
    const response = await fetchJson<{ categories: Category[] }>('/api/categories');
    return response.categories;
  },

  async getSources(category?: CategorySlug): Promise<Source[]> {
    const endpoint = category ? `/api/sources/${category}` : '/api/sources';
    return fetchJson<Source[]>(endpoint);
  },

  async getSummaryHistory(category: CategorySlug): Promise<SummaryHistoryItem[]> {
    return fetchJson<SummaryHistoryItem[]>(`/api/summaries/${category}/history`);
  },

  async getHealth(): Promise<HealthStatus> {
    return fetchJson<HealthStatus>('/api/health');
  },
};

// Cached version of the API
export const cachedSummaryApi = {
  async getSummaryLatest(category: CategorySlug): Promise<SummaryResponse> {
    const cacheKey = `summary:${category}:latest`;
    return summaryCache.getOrSet(cacheKey, () => summaryApi.getSummaryLatest(category));
  },

  async getSummaryByDate(
    category: CategorySlug,
    date: string,
    edition?: string
  ): Promise<SummaryResponse> {
    const cacheKey = `summary:${category}:${date}:${edition || 'default'}`;
    return summaryCache.getOrSet(cacheKey, () => summaryApi.getSummaryByDate(category, date, edition));
  },

  async getSummaryToday(category: CategorySlug): Promise<SummaryResponse> {
    return this.getSummaryLatest(category);
  },

  async getCategories(): Promise<Category[]> {
    return categoryCache.getOrSet('categories', () => summaryApi.getCategories());
  },

  async getSources(category?: CategorySlug): Promise<Source[]> {
    const cacheKey = category ? `sources:${category}` : 'sources:all';
    return sourceCache.getOrSet(cacheKey, () => summaryApi.getSources(category));
  },

  async getSummaryHistory(category: CategorySlug): Promise<SummaryHistoryItem[]> {
    const cacheKey = `history:${category}`;
    return historyCache.getOrSet(cacheKey, () => summaryApi.getSummaryHistory(category));
  },

  async getHealth(): Promise<HealthStatus> {
    return healthCache.getOrSet('health', () => summaryApi.getHealth());
  },
};
